/**
 * The Accessibility tab's issues-over-time card (Asana #26, components/accessibility/issues-over-time): its Chart /
 * Table switch, its legend, and what it sends the chart host.
 *
 * The contracts are PHP's (App\Services\BlinkPages\Accessibility\IssuesOverTime), one for each set of shown lines, so a
 * pill only picks one and sends it: no round trip, and nothing assembled here. The tooltip, its placement and the
 * points' names are the SEO page's trend's own (seriesTooltip(), tooltipPlacement(), what periodLabels() gives the
 * host), so the two pages' trends read the same under the pointer.
 *
 * Blade keys the card by its summary, so a new period mounts a new component with a new host id, and no detail held for
 * the last period's host can be replayed into this one.
 *
 * Prefix: iot_.
 */

const HOST_EVENT = 'superset-chart-data';

/**
 * What the host is sent for the lines left shown: their contract with the SEO trend's tooltip, or, with both switched
 * off, an empty state that says so in words rather than a bare grid that reads as a chart that failed to load.
 *
 * @param {object}    config    IssuesOverTime::view()'s `client`.
 * @param {string[]}  hidden    The keys of the lines the reader switched off.
 * @param {object}    options   The site-analytics-options module, for seriesTooltip().
 * @param {?Function} placement ECharts' `position`: the host's tooltipPlacement().
 */
export function issuesOverTimeDetail(config, hidden, options, placement = null) {
    const shown = config.lines.filter((line) => ! hidden.includes(line.key));

    if (shown.length === 0) {
        return { id: config.hostId, status: 'empty', emptyMessage: config.text.allHidden };
    }

    return {
        id: config.hostId,
        contract: config.contracts[shown.map((line) => line.key).join(',')],
        tooltip: options.seriesTooltip(shown, config.dates, { noDataPeriod: config.text.noScan }, { placement }),
        periods: config.periods,
    };
}

export default function AccessibilityIssuesOverTime(config) {
    let renderToken = 0;
    let placed = null;

    return {
        iot_view: 'chart',
        iot_hidden: [],

        init() {
            // With no scan in the period the card prints a sentence and mounts no host.
            if (Object.keys(config.contracts).length > 0) this.iot_send();
        },

        iot_shown(key) {
            return ! this.iot_hidden.includes(key);
        },

        iot_toggle(key) {
            this.iot_hidden = this.iot_shown(key)
                ? [...this.iot_hidden, key]
                : this.iot_hidden.filter((other) => other !== key);

            this.iot_send();
        },

        async iot_send() {
            const token = ++renderToken;

            // Loaded on the first draw rather than with app.js, as the SEO page loads them.
            const [options, { tooltipPlacement }] = await Promise.all([
                import('../charts/site-analytics-options'),
                import('../charts/tooltip-placement'),
            ]);

            if (token !== renderToken) return;

            // Made on the first hover, when ECharts asks: tooltipPlacement() reads the document.
            const placement = (...args) => (placed ??= tooltipPlacement(config.hostId))(...args);

            window.dispatchEvent(new CustomEvent(HOST_EVENT, {
                detail: issuesOverTimeDetail(config, this.iot_hidden, options, placement),
            }));
        },
    };
}
